import React from 'react';
import { Linkedin, ArrowUpRight, Mail } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

const FooterLink = ({ to, children }: { to: string; children: React.ReactNode }) => (
  <Link to={to} className="text-background/65 hover:text-background transition-colors flex items-center gap-1 group">
    {children}
    <ArrowUpRight className="w-3 h-3 opacity-0 group-hover:opacity-100 transition-opacity" />
  </Link>
);

export const Footer = () => {
  const { t } = useTranslation();
  
  return (
    <footer className="border-t border-background/20 bg-foreground text-primary-foreground">
      <div className="container px-4 md:px-6 py-16 max-w-screen-xl mx-auto">
        {/* Main footer grid */}
        <div className="grid gap-12 md:grid-cols-2 lg:grid-cols-5">
          {/* Brand */}
          <div className="space-y-4 lg:col-span-1">
            <div className="flex items-center gap-2">
              <span className="text-xl font-display font-bold text-primary-foreground">
                NoQ5<span className="font-sans font-medium">.ai</span>
              </span>
            </div>
            <p className="text-sm text-background/65 leading-relaxed">
              AI Chief of Staff for Revenue. Autonomous CRM updates, forecasting, and pipeline intelligence for sales-driven companies. Built in Ireland, GDPR compliant.
            </p>
            <div className="flex items-center gap-3 pt-2">
              <a href="https://www.linkedin.com/company/noq5/" target="_blank" rel="noopener noreferrer" className="w-10 h-10 border border-background/25 flex items-center justify-center text-background/65 hover:text-background transition-colors" aria-label="Follow NoQ5.ai on LinkedIn">
                <Linkedin className="w-4 h-4" />
              </a>
              <a href="mailto:batiste@noq5.ai" className="w-10 h-10 border border-background/25 flex items-center justify-center text-background/65 hover:text-background transition-colors" aria-label="Email NoQ5.ai">
                <Mail className="w-4 h-4" />
              </a>
            </div>
          </div>
          
          {/* Product */}
          <div className="space-y-4">
            <h3 className="text-sm font-semibold uppercase tracking-wider text-primary-foreground">Product</h3>
            <div className="space-y-3 text-sm">
              <FooterLink to="/product/platform">Core Platform</FooterLink>
              
              <FooterLink to="/product/command-center">Command Center</FooterLink>
              <FooterLink to="/pricing">Pricing</FooterLink>
              <FooterLink to="/integrations">Integrations</FooterLink>
              <FooterLink to="/crm-agent">CRM AI Agent</FooterLink>
              <FooterLink to="/app">Try App</FooterLink>
              <FooterLink to="/trial">Free Trial</FooterLink>
            </div>
          </div>

          {/* Solutions */}
          <div className="space-y-4">
            <h3 className="text-sm font-semibold uppercase tracking-wider text-primary-foreground">Solutions</h3>
            <div className="space-y-3 text-sm">
              <FooterLink to="/ai-crm-automation">AI CRM Automation</FooterLink>
              <FooterLink to="/ai-sales-ops">AI Sales Ops</FooterLink>
              <FooterLink to="/pipeline-management-ai">Pipeline Management AI</FooterLink>
              <FooterLink to="/lead-qualification-ai">Lead Qualification AI</FooterLink>
              <FooterLink to="/crm-enrichment-agent">CRM Enrichment Agent</FooterLink>
              <FooterLink to="/crm-data-entry-automation">Data Entry Automation</FooterLink>
              <FooterLink to="/agentic-ai-sales">Agentic AI Sales</FooterLink>
              <FooterLink to="/ai-workflow-automation">AI Workflow Automation</FooterLink>
              <FooterLink to="/ai-for-cros">AI for CROs</FooterLink>
              <FooterLink to="/replace-crm-manual-work">Replace Manual CRM Work</FooterLink>
            </div>
          </div>
          
          {/* Resources */}
          <div className="space-y-4">
            <h3 className="text-sm font-semibold uppercase tracking-wider text-primary-foreground">Resources</h3>
            <div className="space-y-3 text-sm">
              <FooterLink to="/blog">Blog</FooterLink>
              <FooterLink to="/comparison">Compare Alternatives</FooterLink>
              <FooterLink to="/docs">Documentation</FooterLink>
              <FooterLink to="/faq">FAQ</FooterLink>
              <FooterLink to="/resources/cro-pipeline-playbook">CRO Pipeline Playbook</FooterLink>
              <FooterLink to="/about">About Us</FooterLink>
              <FooterLink to="/why-crm-data-is-broken">Why CRM Data Is Broken</FooterLink>
              <FooterLink to="/for-ai">For AI</FooterLink>
            </div>
          </div>
          
          {/* Legal & Trust */}
          <div className="space-y-4">
            <h3 className="text-sm font-semibold uppercase tracking-wider text-primary-foreground">{t('footer.legal')}</h3>
            <div className="space-y-3 text-sm">
              <FooterLink to="/security">Security & Trust</FooterLink>
              <FooterLink to="/privacy-policy">{t('footer.privacyPolicy')}</FooterLink>
              <FooterLink to="/terms-of-service">{t('footer.termsOfService')}</FooterLink>
              <FooterLink to="/dpa">Data Processing Agreement</FooterLink>
              <FooterLink to="/subprocessors">Subprocessors</FooterLink>
              <FooterLink to="/privacy-eu">EU Privacy (DPIA)</FooterLink>
            </div>
            <div className="pt-4 space-y-2">
              <div className="flex items-center gap-2 text-xs text-muted-foreground">
                <span className="text-base">🇪🇺</span>
                <span>EU-hosted · GDPR compliant</span>
              </div>
              <div className="flex items-center gap-2 text-xs text-muted-foreground">
                <span className="text-base">🔒</span>
                <span>SOC 2 Type I targeted Q3 2026</span>
              </div>
            </div>
          </div>
        </div>

        {/* Extended link sections — ALL pages */}
        <div className="mt-12 pt-8 border-t border-border">
          <div className="grid gap-8 md:grid-cols-2 lg:grid-cols-4">
            {/* CRM Integrations */}
            <div className="space-y-3">
              <h4 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">CRM Integrations</h4>
              <div className="space-y-2 text-sm">
                <FooterLink to="/ai-agent-salesforce">Salesforce AI Agent</FooterLink>
                <FooterLink to="/hubspot-ai-agent">HubSpot AI Agent</FooterLink>
                <FooterLink to="/pipedrive-ai-agent">Pipedrive AI Agent</FooterLink>
                <FooterLink to="/zoho-ai-agent">Zoho AI Agent</FooterLink>
                <FooterLink to="/integrations/salesforce">Salesforce Integration</FooterLink>
                <FooterLink to="/integrations/hubspot">HubSpot Integration</FooterLink>
                <FooterLink to="/integrations/pipedrive">Pipedrive Integration</FooterLink>
                <FooterLink to="/integrations/google-workspace">Google Workspace</FooterLink>
                <FooterLink to="/integrations/microsoft-365">Microsoft 365</FooterLink>
                <FooterLink to="/integrations/slack">Slack</FooterLink>
                <FooterLink to="/integrations/zoom">Zoom</FooterLink>
                <FooterLink to="/integrations/linkedin">LinkedIn</FooterLink>
              </div>
            </div>

            {/* Comparisons */}
            <div className="space-y-3">
              <h4 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Comparisons</h4>
              <div className="space-y-2 text-sm">
                <FooterLink to="/noq5-vs-relevance-ai">NoQ5 vs Relevance AI</FooterLink>
                <FooterLink to="/noq5-vs-agentforce">NoQ5 vs Agentforce</FooterLink>
                <FooterLink to="/noq5-vs-hubspot-ai">NoQ5 vs HubSpot AI</FooterLink>
                <FooterLink to="/apollo-vs-noq5">Apollo vs NoQ5</FooterLink>
                <FooterLink to="/zoominfo-vs-noq5">ZoomInfo vs NoQ5</FooterLink>
                <FooterLink to="/comparison/salesforce-einstein">vs Salesforce Einstein</FooterLink>
                <FooterLink to="/comparison/hubspot">vs HubSpot</FooterLink>
              </div>
            </div>

            {/* Automations */}
            <div className="space-y-3">
              <h4 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Automations</h4>
              <div className="space-y-2 text-sm">
                <FooterLink to="/automate-lead-enrichment">Lead Enrichment</FooterLink>
                <FooterLink to="/automate-crm-updates">CRM Updates</FooterLink>
                <FooterLink to="/automate-sales-notes">Sales Notes</FooterLink>
                <FooterLink to="/automate-follow-ups">Follow-Ups</FooterLink>
                <FooterLink to="/automate-deal-tracking">Deal Tracking</FooterLink>
                <FooterLink to="/automate-contact-sync">Contact Sync</FooterLink>
                <FooterLink to="/automate-data-cleaning">Data Cleaning</FooterLink>
                <FooterLink to="/auto-update-salesforce">Auto Update Salesforce</FooterLink>
                <FooterLink to="/fill-crm-data-automatically">Fill CRM Data</FooterLink>
                <FooterLink to="/auto-log-calls-crm">Auto Log Calls</FooterLink>
                <FooterLink to="/sync-crm-with-email">Sync CRM with Email</FooterLink>
              </div>
            </div>

            {/* Industries & Use Cases */}
            <div className="space-y-3">
              <h4 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Industries</h4>
              <div className="space-y-2 text-sm">
                <FooterLink to="/ai-crm-for-saas">SaaS</FooterLink>
                <FooterLink to="/ai-crm-for-fintech">Fintech</FooterLink>
                <FooterLink to="/ai-crm-for-healthcare">Healthcare</FooterLink>
                <FooterLink to="/ai-crm-for-recruitment">Recruitment</FooterLink>
                <FooterLink to="/ai-crm-for-real-estate">Real Estate</FooterLink>
              </div>
              <h4 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground pt-4">Use Cases</h4>
              <div className="space-y-2 text-sm">
                <FooterLink to="/use-cases">All Use Cases</FooterLink>
                <FooterLink to="/use-cases/crm-automation">CRM Automation</FooterLink>
                <FooterLink to="/use-cases/deal-forecasting">Deal Forecasting</FooterLink>
                <FooterLink to="/use-cases/pipeline-management">Pipeline Management</FooterLink>
                <FooterLink to="/use-cases/revenue-intelligence">Revenue Intelligence</FooterLink>
                <FooterLink to="/use-cases/sales-coaching">Sales Coaching</FooterLink>
                <FooterLink to="/use-cases/business-continuity">Business Continuity</FooterLink>
              </div>
            </div>
          </div>
        </div>

        {/* Blog posts */}
        <div className="mt-8 pt-8 border-t border-border">
          <h4 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-4">Latest Articles</h4>
          <div className="grid gap-2 md:grid-cols-2 lg:grid-cols-4 text-sm">
            <FooterLink to="/blog/crm-automation-guide">CRM Automation Guide</FooterLink>
            <FooterLink to="/blog/why-sales-reps-hate-crms">Why Sales Reps Hate CRMs</FooterLink>
            <FooterLink to="/blog/ai-copilots-sales-teams-2025">AI Copilots for Sales 2025</FooterLink>
            <FooterLink to="/blog/salesforce-data-quality-ai-voice-assistants">Salesforce Data Quality</FooterLink>
            <FooterLink to="/blog/hubspot-vs-salesforce-ai-integration-2025">HubSpot vs Salesforce AI</FooterLink>
            <FooterLink to="/blog/voice-assistant-technology-sales-teams-roi-calculator">Voice Tech ROI</FooterLink>
            <FooterLink to="/blog/best-salesforce-ai-copilot-smbs-2025">Best Salesforce AI for SMBs</FooterLink>
            <FooterLink to="/blog/update-salesforce-automatically-2025">Update Salesforce Auto 2025</FooterLink>
            <FooterLink to="/blog/gdpr-compliant-salesforce-ai-copilot-europe">GDPR Salesforce AI Copilot</FooterLink>
            <FooterLink to="/blog/how-to-update-salesforce-automatically">How to Update Salesforce</FooterLink>
          </div>
        </div>
        
        {/* Bottom bar */}
        <div className="mt-12 pt-8 border-t border-border flex flex-col sm:flex-row items-center justify-between gap-4">
          <div className="text-sm text-muted-foreground">
            © {new Date().getFullYear()} NoQ5.ai Technologies Ltd. All rights reserved.
          </div>
          <div className="text-sm text-muted-foreground">
            Made with ❤️ in Dublin, Ireland
          </div>
        </div>
      </div>
    </footer>
  );
};
